import React, { type ReactNode } from 'react';
/* FIXME: the copy below was written against SUI Typography and Link, but these
/* are MUI v4, which ignores the SUI variant and color values and has rendered
/* the FAQ copy unstyled since 05ed744. variant="inherit" and color="initial"
/* hold that output as it is so the conversion stays behaviour-neutral; they
/* are placeholders, not a styling choice. The fix is to move to SUI. */
import { Link, Typography } from '@material-ui/core';
import {
  Star,
  Savings,
  Online,
  Checkmark,
  InfoFilled,
} from '@one-thd/sui-icons';
import { track } from '../analytics/analytics';
import { FAQ_LINKS } from './analytics';
import { getCheckoutAccessTypeLabel } from './general';

// The eligibility banner links to this section by an id derived from the title,
// so the two have to come from one place - a copy edit here would otherwise
// leave the link pointing at an anchor that no longer exists.
export const FAQ_WHO_CAN_QUALIFY = 'Who Can Qualify';

export interface Faq {
  question: string;
  answer: ReactNode;
  /* Sent with FAQ_EXPEND when the question is opened. */
  analyticsValue: string;
}

export interface FaqSection {
  title: string;
  FAQs: Faq[];
}

interface FaqOptions {
  enableHdPass?: boolean;
  isIdmeEnabled?: boolean;
}

/* A row for common/List. `description` is only shown by the 'md' variant. */
export interface ListItem {
  title: string;
  description?: string;
  icon?: ReactNode;
}

export const UNVERIFIED_ADMIN = 'unverified-admin';
export const UNVERIFIED_PURCHASER = 'unverified-purchaser';
export const UNVERIFIED_DIY = 'unverified-DIY';
export const VERIFIED_ADMIN = 'verified-admin';
export const VERIFIED_PURCHASER = 'verified-purchaser';
export const VERIFIED_USER = 'verified-user';

export const ROUTES = {
  home: {
    label: 'Home',
    url: '/',
  },
  landing: {
    label: 'Military Discount Benefit',
    url: '/military/discount',
  },
  verification: {
    label: 'Verification',
    url: '/military/discount',
  },
  b2bAccount: {
    label: 'Account',
    url: '/b2b/account/view/landingpage',
  },
  b2cAccount: {
    label: 'Account',
    url: '/myaccount/profile',
  },
  b2cAccountMenu: {
    label: 'Account',
    url: '/myaccount/profile/menu',
  },
  sheerId: {
    label: 'SheerID',
    url: 'https://offers.sheerid.com/sheerid/help-center/?pid=6179ae2959e1633ef316ed52',
  },
  shopMyExchange: {
    label: 'ShopMyExchange.com',
    url: 'https://www.shopmyexchange.com/a/3539981?iid=home-depot',
  },
  myNavyExchange: {
    label: 'MyNavyExchange.com',
    url: 'https://www.mynavyexchange.com/homedepot',
  },
  idmeHelpCenter: {
    label: 'ID.me Help Center',
    url: 'https://help.id.me/hc/en-us/articles/204456674-Verify-your-Military-status-with-ID-me-for-discounts',
  },
  idmeSupport: {
    label: 'ID.me Support',
    url: 'https://id.me/',
  },
  termsOfUse: {
    label: 'Terms of Use',
    url: 'https://www.homedepot.com/c/Terms_of_Use',
  },
  privacySecurity: {
    label: 'Privacy & Security Statement',
    url: 'https://www.homedepot.com/privacy/privacy-and-security-statement',
  },
};

const militaryBranchList = (
  <ul className="sui-pl-4 sui-mb-4">
    <li>&mdash; Army</li>
    <li>&mdash; Navy</li>
    <li>&mdash; Air Force</li>
    <li>&mdash; Marine Corps</li>
    <li>&mdash; Coast Guard</li>
    <li>&mdash; Space Force</li>
    <li>&mdash; National Oceanic and Atmospheric Administration (NOAA)</li>
    <li>&mdash; US Public Health Service Commissioned Corps (USPHS)</li>
    <li>&mdash; National Guard</li>
  </ul>
);

// The copy doc repeats these verification steps verbatim under both Who Can
// Qualify and Account Maintenance, with only the subject differing. Keep them
// in one place so a future copy edit can't land in only one of the two.
const spouseVerificationCopy = (subject: string) => (
  <>
    {subject} must verify with their first name, last name, The Home Depot
    account email, and the service member&apos;s required information. On the
    Military Discount &amp; Appreciation page under Military Discount, select
    &ldquo;Get Started&rdquo; and follow the on-screen instructions to enroll.
  </>
);

const whoCanQualifyIdmeContent: FaqSection = {
  title: FAQ_WHO_CAN_QUALIFY,
  FAQs: [
    {
      question: 'Who is eligible for the Military Discount?',
      answer: (
        <>
          <Typography variant="inherit">
            Active-Duty, reservist, veterans and retirees of the following
            branches of the United States military:
          </Typography>
          {militaryBranchList}
          <Typography variant="inherit">
            The Military Discount is also available to spouses of eligible
            service members (including Gold Star spouses).{' '}
            {spouseVerificationCopy('Eligible spouses')}
          </Typography>
        </>
      ),
      analyticsValue: 'who is eligible',
    },
    {
      question: 'Who is not eligible for the Military Discount?',
      answer:
        'The following are not eligible to receive the Military Discount: non-spousal dependents of active military and veterans, dishonorably discharged veterans, non-military members, auxiliary members and civilian employees of the military.',
      analyticsValue: 'who is not eligible',
    },
  ],
};

const whoCanQualifyContent: FaqSection = {
  title: FAQ_WHO_CAN_QUALIFY,
  FAQs: [
    {
      question: 'Who is eligible for the Military Discount?',
      answer: (
        <>
          <Typography variant="inherit">
            {' '}
            Active and reservist members, veterans and retirees of the following
            branches of the United States military:
          </Typography>
          {militaryBranchList}
          <Typography variant="inherit">
            The Military Discount is also available to the current spouse of an
            eligible service member and Gold Star spouses.
          </Typography>
        </>
      ),
      analyticsValue: 'who is eligible',
    },
    {
      question: 'Who is not eligible for the Military Discount?',
      answer:
        'The following are not eligible to receive the Military Discount: non-spousal dependents of active military and veterans; dishonorably discharged veterans; non-military members; auxiliary members; and civilian employees of the military.',
      analyticsValue: 'who is not eligible',
    },
    {
      question: 'Are military spouses eligible to use the Military Discount?',
      answer: (
        <Typography variant="inherit">
          Yes. Spouses of eligible service members (includes Gold Star spouses)
          must verify with their first name, last name, account email with The
          Home Depot and the service member&apos;s required information. Visit{' '}
          <Link
            underline="always"
            color="initial"
            href="#top"
            onClick={() =>
              track(FAQ_LINKS, { value: 'Military Discount Benefit' })
            }
          >
            Military Discount Benefit
          </Link>
          , select &ldquo;Get Started&rdquo; and follow the on-screen
          instructions to enroll.
        </Typography>
      ),
      analyticsValue: 'spouse eligibility',
    },
  ],
};

const howToEnrollIdmeContent: FaqSection = {
  title: 'How To Enroll',
  FAQs: [
    {
      question: 'How can I enroll in the Military Discount?',
      answer: (
        <>
          <Typography variant="inherit">
            To enroll, visit the Military Discount Benefit page or open The Home
            Depot App. If you have an existing Home Depot account, log in to
            your existing personal account or create a new account. After you
            have a Home Depot account, follow the steps with ID.me to verify.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="inherit">
              Under Military Discount, select &ldquo;Get Started&rdquo; and
              follow the steps with ID.me to verify your military status. You
              will need to sign in to your ID.me account or create a new one if
              you&apos;re new to ID.me. Once you complete all steps and are
              verified, your account on HomeDepot.com will reflect your enrolled
              Military Discount status.
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'how can i enroll',
    },
    {
      question: 'How does The Home Depot verify proof of military service?',
      answer: (
        <>
          <Typography variant="inherit">
            The Home Depot partners with ID.me, a trusted third party vendor, to
            confirm your military status using private and secure data sources.
            The verification process typically takes a few minutes.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="inherit">
              You only need to verify your military status once. After your
              account is set up, you can access the Military Discount on all
              eligible purchases.
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'verify proof of service',
    },
    {
      question: 'What do I do if I have issues enrolling?',
      answer: (
        <>
          <Typography variant="inherit">
            If you&apos;re having trouble verifying your military status, visit
            the{' '}
            <Link
              underline="always"
              color="initial"
              target="_blank"
              rel="noopener noreferrer"
              href={ROUTES.idmeHelpCenter.url}
              onClick={() =>
                track(FAQ_LINKS, { value: ROUTES.idmeHelpCenter.label })
              }
            >
              {ROUTES.idmeHelpCenter.label}
            </Link>{' '}
            for troubleshooting steps and a list of accepted documents.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="inherit">
              If you still need help,{' '}
              <Link
                underline="always"
                color="initial"
                target="_blank"
                rel="noopener noreferrer"
                href={ROUTES.idmeSupport.url}
                onClick={() =>
                  track(FAQ_LINKS, { value: ROUTES.idmeSupport.label })
                }
              >
                contact {ROUTES.idmeSupport.label}
              </Link>
              .
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'issues enrolling',
    },
  ],
};

const howToEnrollContent: FaqSection = {
  title: 'How To Enroll',
  FAQs: [
    {
      question: 'How can I enroll in the Military Discount?',
      answer: (
        <Typography variant="inherit">
          To enroll, visit{' '}
          <Link
            href="#top"
            underline="always"
            color="initial"
            onClick={() =>
              track(FAQ_LINKS, { value: 'Military Discount Benefit' })
            }
          >
            Military Discount Benefit
          </Link>{' '}
          or open The Home Depot mobile app. Log in to your existing personal
          account or create a new account. Go to the Military Discount Benefit
          page, select &ldquo;Get Started&rdquo; and follow the on-screen
          instructions to complete your military status verification. Once you
          complete all steps, your personal account on HomeDepot.com will
          reflect your enrolled Military Discount status.
        </Typography>
      ),
      analyticsValue: 'how can i enroll',
    },
    {
      question: 'How does The Home Depot verify proof of military service?',
      answer: (
        <>
          <Typography variant="inherit">
            The Home Depot partners with SheerID, our trusted third-party
            verification service, to use private and secure data sources to
            confirm the accuracy of the information you provide. The
            verification process typically takes a few minutes. In an effort to
            assist our customers comply with law, SheerID cannot accept Military
            IDs from currently serving Military personnel or employees of the
            Federal Government to verify military status. If prompted for
            documents, please upload a different document to verify your
            military status.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="inherit">
              You only need to verify your military status once. Once your
              account is set up, you may access the Military Discount for all
              eligible purchases.
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'verify proof of service',
    },
    {
      question: 'What do I do if I have issues enrolling?',
      answer: (
        <Typography variant="inherit">
          Visit{' '}
          <Link
            underline="always"
            color="initial"
            target="_blank"
            rel="noopener noreferrer"
            href={ROUTES.sheerId.url}
            onClick={() => track(FAQ_LINKS, { value: ROUTES.sheerId.label })}
          >
            {ROUTES.sheerId.label}
          </Link>{' '}
          for assistance with the registration process and/or to get additional
          information on the documents required for verification.
        </Typography>
      ),
      analyticsValue: 'issues enrolling',
    },
  ],
};

const usingMilitaryDiscountIdmeContent: FaqSection = {
  title: 'Using the Military Discount',
  FAQs: [
    {
      question: 'How do I use my Military Discount?',
      answer: (
        <Typography variant="inherit">
          To use your discount online, log in to your account on HomeDepot.com.
          You&apos;ll see the Military Discount automatically applied to
          eligible items in your cart at checkout. To use the discount in-store,
          DIY customers can scan their Virtual ID from The Home Depot App or
          enter the phone number registered with the account at checkout. Pro
          Xtra members can scan their HD Pass from The Home Depot App or enter
          the phone number registered with the account. Your Military Discount
          will be applied to eligible items during checkout.
        </Typography>
      ),
      analyticsValue: 'how do i use',
    },
    {
      question: 'How much can I save with the Military Discount?',
      answer: (
        <Typography variant="inherit">
          The Military Discount applies 10% off the price of select in-stock
          purchases made by eligible participants. Eligible participants receive
          a $400 maximum annual discount that resets each calendar year.
        </Typography>
      ),
      analyticsValue: 'how much can i save',
    },
    {
      question: 'What items are excluded from the Military Discount?',
      answer:
        "The Military Discount can't be applied to select items, services and fees. It excludes, but isn't limited to, some commodity products (such as lumber, wire, building materials), value priced merchandise, appliances, special order items, rental fees, labor items, gift cards and services including freight, delivery and installation.",
      analyticsValue: 'what items excluded',
    },
    {
      question:
        'Can I purchase appliances using The Home Depot Military Discount?',
      answer: (
        <>
          <Typography variant="inherit">
            Most in-store and online appliances are excluded from our discount
            benefit.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="inherit">
              Veterans, active-duty, retirees, Department of War (DoW)
              civilians, National Guard, reservists and dependents may purchase
              The Home Depot appliances through our partnership with the
              Military Exchanges. Through this partnership, shoppers can enjoy
              special military pricing and tax-free purchases on more than 2
              million items. Visit{' '}
              <Link
                underline="always"
                color="initial"
                target="_blank"
                rel="noopener noreferrer"
                href={ROUTES.shopMyExchange.url}
                onClick={() =>
                  track(FAQ_LINKS, { value: ROUTES.shopMyExchange.label })
                }
              >
                {ROUTES.shopMyExchange.label}
              </Link>{' '}
              or{' '}
              <Link
                underline="always"
                color="initial"
                target="_blank"
                rel="noopener noreferrer"
                href={ROUTES.myNavyExchange.url}
                onClick={() =>
                  track(FAQ_LINKS, { value: ROUTES.myNavyExchange.label })
                }
              >
                {ROUTES.myNavyExchange.label}
              </Link>{' '}
              to learn more.
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'can i purchase appliance',
    },
  ],
};

const getUsingMilitaryDiscountContent = (
  ENABLE_HD_PASS_VERBIAGE?: boolean,
): FaqSection => {
  const checkoutAccessType = getCheckoutAccessTypeLabel(
    ENABLE_HD_PASS_VERBIAGE,
  );

  return {
    title: 'Using the Military Discount',
    FAQs: [
      {
        question: 'How do I use my Military Discount?',
        answer: (
          <>
            <Typography variant="inherit">
              To use it online, log in to your account on HomeDepot.com.
              You&apos;ll see the Military Discount automatically applied to
              eligible items in cart during checkout.
            </Typography>
            <div className="sui-mt-4">
              <Typography variant="inherit">
                To use in store, go to the checkout register. Scan your{' '}
                {checkoutAccessType} from The Home Depot App or enter the phone
                number you registered with The Home Depot account. Your Military
                Discount will be applied to eligible items during checkout.
              </Typography>
            </div>
          </>
        ),
        analyticsValue: 'how do i use',
      },
      {
        question: 'How much can I save with the Military Discount?',
        answer: (
          <Typography variant="inherit">
            The Military Discount applies 10% off the price of select in-stock
            purchases made by eligible participants. Eligible participants
            receive a $400 maximum annual discount that resets each calendar
            year.
          </Typography>
        ),
        analyticsValue: 'how much can i save',
      },
      {
        question: 'What items are excluded from the Military Discount?',
        answer:
          "The Military Discount can't be applied to select items, services and fees. It includes, but isn't limited to, some commodity products (including lumber, wire, building materials), value priced merchandise, appliances, special order items, tool rental fees, labor items, gift cards and services including freight and delivery.",
        analyticsValue: 'what items excluded',
      },
      {
        question: 'Can I purchase appliances using the Military Discount?',
        answer: (
          <>
            <Typography variant="inherit">
              Most in-store and online appliances are excluded from our discount
              benefit.
            </Typography>
            <div className="sui-mt-4">
              <Typography variant="inherit">
                Active-duty service members, dependents, retirees, Department of
                Defense civilians and retirees, and honorably discharged
                veterans may purchase Home Depot appliances through a
                partnership with Military Exchanges.
              </Typography>
            </div>
            <div className="sui-mt-4">
              <Typography variant="inherit">
                Shoppers enjoy special military pricing and tax-free purchases.
              </Typography>
            </div>
            <div className="sui-mt-4">
              <Typography variant="inherit">
                Visit{' '}
                <Link
                  underline="always"
                  color="initial"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={ROUTES.shopMyExchange.url}
                  onClick={() =>
                    track(FAQ_LINKS, { value: ROUTES.shopMyExchange.label })
                  }
                >
                  {ROUTES.shopMyExchange.label}
                </Link>{' '}
                or{' '}
                <Link
                  underline="always"
                  color="initial"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={ROUTES.myNavyExchange.url}
                  onClick={() =>
                    track(FAQ_LINKS, { value: 'MyNavyExchange.com' })
                  }
                >
                  MyNavyExchange.com
                </Link>{' '}
                to learn more.
              </Typography>{' '}
            </div>
          </>
        ),
        analyticsValue: 'can i purchase appliance',
      },
    ],
  };
};

const accountMaintenanceIdmeContent: FaqSection = {
  title: 'Account Maintenance',
  FAQs: [
    {
      question:
        'Can a spouse and military service member share one account with The Home Depot and receive the Military Discount?',
      answer: (
        <Typography variant="inherit">
          Eligible service members and eligible spouses must have separate
          personal accounts on{' '}
          <Link
            underline="always"
            color="initial"
            href={ROUTES.home.url}
            onClick={() => track(FAQ_LINKS, { value: 'HomeDepot.com' })}
          >
            HomeDepot.com
          </Link>{' '}
          and enroll in the Military Discount separately. Eligible spouses can
          register for their own account with The Home Depot under Military
          Discount.{' '}
          {spouseVerificationCopy(
            'Spouses of eligible service members (including Gold Star spouses)',
          )}{' '}
          The Home Depot reserves the right to re-verify military status.
        </Typography>
      ),
      analyticsValue: 'spouse share one account',
    },
  ],
};

const accountMaintenanceContent: FaqSection = {
  title: 'Account Maintenance',
  FAQs: [
    {
      question:
        'Can a spouse and military service member share one account with The Home Depot and receive the Military Discount?',
      answer: (
        <>
          <Typography variant="inherit">
            Eligible service members and eligible spouses must have separate
            personal accounts on HomeDepot.com and enroll in the Military
            Discount separately.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="inherit">
              Eligible spouses can register for their own account with The Home
              Depot at{' '}
              <Link
                underline="always"
                color="initial"
                href="#top"
                onClick={() =>
                  track(FAQ_LINKS, { value: 'Military Discount Benefit' })
                }
              >
                Military Discount Benefit
              </Link>
              .
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'spouse share one account',
    },
  ],
};

export const getUnenrolledGroupedFAQs = ({
  enableHdPass,
  isIdmeEnabled,
}: FaqOptions = {}): FaqSection[] =>
  isIdmeEnabled
    ? [
        whoCanQualifyIdmeContent,
        howToEnrollIdmeContent,
        usingMilitaryDiscountIdmeContent,
        accountMaintenanceIdmeContent,
      ]
    : [
        whoCanQualifyContent,
        howToEnrollContent,
        getUsingMilitaryDiscountContent(enableHdPass),
        accountMaintenanceContent,
      ];

export const getEnrolledGroupedFAQs = ({
  enableHdPass,
  isIdmeEnabled,
}: FaqOptions = {}): FaqSection[] =>
  isIdmeEnabled
    ? [
        usingMilitaryDiscountIdmeContent,
        accountMaintenanceIdmeContent,
        whoCanQualifyIdmeContent,
        howToEnrollIdmeContent,
      ]
    : [
        getUsingMilitaryDiscountContent(enableHdPass),
        accountMaintenanceContent,
        whoCanQualifyContent,
        howToEnrollContent,
      ];

export const faqLegalCopy = (ENABLE_TC_CHANGES: boolean) => (
  <>
    <span>
      *Eligible participants receive a $400 maximum annual discount which resets
      each calendar year.{' '}
    </span>
    {ENABLE_TC_CHANGES ? (
      <span>
        {' '}
        The discount cannot be combined with other promotions, or applied to
        clearance items or prior purchases, and is for personal use only.{' '}
      </span>
    ) : (
      <span>
        {' '}
        The discount cannot be combined with other promotions or previous
        purchases and is for personal use only.{' '}
      </span>
    )}
    <span>
      The Home Depot reserves the right to change eligibility requirements or
      terminate the discount program at any time, with or without prior notice
      to eligible participants. Any changes will be effective immediately, and
      participants waive any right to receive specific notice of such changes.
      The Home Depot reserves the right to revoke enrollment status based on
      alleged, suspected and/or actual evidence of misuse or fraud in our sole
      and absolute discretion. Offer is not redeemable for cash, is
      non-transferable and cannot be combined with manager-discretion price
      adjustments. Additional exclusions may apply. See store for details.
    </span>
  </>
);

export const idmeFaqLegalCopy = () => (
  <>
    <Typography>
      <b>Military Discount Terms</b>
    </Typography>
    <div className="sui-mt-1">
      <Typography>
        Welcome to The Home Depot Military Discount Program (the
        &ldquo;Program&rdquo;). These Terms govern your participation in the
        Program.
      </Typography>
    </div>
    <div className="sui-mt-2">
      <Typography>
        1. Benefits: Eligible participants receive a $400 maximum annual
        discount which resets each calendar year. The discount cannot be
        combined with other promotions or applied to clearance items or prior
        purchases and is for personal use only. The Home Depot reserves the
        right to change eligibility requirements or terminate the discount
        program at any time, with or without prior notice to eligible
        participants. Any changes will be effective immediately, and
        participants waive any right to receive specific notice of such changes.
        The Home Depot reserves the right to revoke enrollment status based on
        alleged, suspected and/or actual evidence of misuse or fraud in our sole
        and absolute discretion. Offer is not redeemable for cash, is
        non-transferable and cannot be combined with manager-discretion price
        adjustments. Additional exclusions may apply &ndash; see the above FAQ
        for more details.
      </Typography>
    </div>
    <div className="sui-mt-2">
      <Typography>
        2. Reverification: The Home Depot and/or its authorized verification
        vendor reserve the right, at any time and in their sole discretion, to
        require re-verification of a participant&apos;s military or veteran
        status and continued eligibility for the Program. Failure to
        successfully complete any requested re-verification process or provide
        satisfactory proof of eligibility may result in suspension or
        termination of enrollment and discount benefits.
      </Typography>
    </div>
    <div className="sui-mt-2">
      <Typography>
        3. General: Your participation in the Program is also governed by our
        website{' '}
        <Link
          underline="always"
          color="initial"
          target="_blank"
          rel="noopener noreferrer"
          href={ROUTES.termsOfUse.url}
          onClick={() => track(FAQ_LINKS, { value: ROUTES.termsOfUse.label })}
        >
          {ROUTES.termsOfUse.label}
        </Link>
        , which are incorporated by reference herein. We collect and use
        personal information in accordance with our{' '}
        <Link
          underline="always"
          color="initial"
          target="_blank"
          rel="noopener noreferrer"
          href={ROUTES.privacySecurity.url}
          onClick={() =>
            track(FAQ_LINKS, { value: ROUTES.privacySecurity.label })
          }
        >
          {ROUTES.privacySecurity.label}
        </Link>
        .
      </Typography>
    </div>
  </>
);

export const onlyAdminEnrollText =
  'Only Pro account admins can enroll in the Military Discount.';
export const maxDiscountText =
  'You’ve reached your annual savings limit. The discount limit will reset on January 1.';

export const unverifiedAdminCheckList: ListItem[] = [
  {
    title: onlyAdminEnrollText,
    icon: <Checkmark size="small" color="brand" />,
  },
  {
    title: 'Only one Military Discount can be applied per Pro account.',
    icon: <Checkmark size="small" color="brand" />,
  },
  {
    title: 'Discount applies to purchases made by any user on the account.',
    icon: <Checkmark size="small" color="brand" />,
  },
];

export const unverifiedPurchaserCheckList: ListItem[] = [
  {
    title: onlyAdminEnrollText,
    icon: <InfoFilled size="small" color="info" />,
  },
];

export const unverifiedBenefitsList: ListItem[] = [
  {
    title: 'Verify Military Status',
    description:
      'You may be required to provide proof of status with a government-issued document.',
    icon: <Star />,
  },
  {
    title: 'Check Your Email',
    description: 'Receive an email when your status is confirmed.',
    icon: <Online />,
  },
  {
    title: 'Enjoy Your 10% Savings',
    description:
      'Shop online or in store to receive your discount on eligible purchases.',
    icon: <Savings />,
  },
];

export const getVerifiedBenefitsList = (
  checkoutAccessType: string,
): ListItem[] => [
  {
    title: 'Shop Online',
    description: 'See your discount automatically applied in cart.',
  },
  {
    title: 'Shop in Store',
    description: `Apply your discount at the checkout register. Scan your ${checkoutAccessType} from The Home Depot App or enter your phone number.`,
  },
];
